import {
  Center,
  Drawer,
  DrawerContent,
  DrawerOverlay,
  Icon,
  IconButton,
  Spinner,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { PageHeading } from '@duke-hq/ui/components/PageHeading';
import { ArrowRightFromLine, Maximize2, Minimize2, X } from 'lucide-react';
import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AssessmentsList } from './AssessmentsList';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { Trans, Plural } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { SearchInput } from '@duke-hq/ui/components/SearchInput';
import { Details as AssessmentDetails } from './AssessmentView/Details';
import { CenteredSpinner } from '@duke-hq/app-admin-web/components/Loading';
import { constructURL, stringifyValues } from '@duke-hq/lib';
import { usePagination } from '@duke-hq/ui/components/hooks/usePagination';
import { Pagination } from '@duke-hq/ui/components/Pagination/Pagination';
import { useSubSidebar } from '@duke-hq/ui/components/SubSidebar/useSubSidebar';
import TableViewLayout from '@duke-hq/ui/components/TableViewLayout/TableViewLayout';
import { emptyPaginationResponse, SortOrder } from '@duke-hq/lib/pagination';
import { AssessmentOutcomeSchema } from '@duke-hq/svc-api-db/schemas/enums/AssessmentOutcome.schema';
import pickBy from 'lodash-es/pickBy.js';

import useFiltersContext from '@duke-hq/filters/context/useFiltersContext';
import FilterBarLayout from '@duke-hq/filters/components/FilterBarLayout';
import ContractTypeFilter from '@duke-hq/filters/components/ContractTypeFilter';
import CourseFilter from '@duke-hq/app-admin-web/components/FilterBar/components/admin/CourseFilter';
import AssessorFilter from '@duke-hq/app-admin-web/components/FilterBar/components/admin/AssessorFilter';
import OutcomeFilter from '@duke-hq/app-admin-web/components/FilterBar/components/admin/OutcomeFilter';
import ProvidersFilter from '@duke-hq/app-admin-web/components/FilterBar/components/admin/ProvidersFilter';
import { MAX_FILTER_ENTRIES } from '@duke-hq/shared-constants/searchCaps';
import { withPermission } from '../withPermission';

interface SortFilters {
  orderBy?: string | undefined;
  sort?: SortOrder | undefined;
  page?: string | undefined;
  search?: string | undefined;
  outcome?: string | undefined;
  courseIds?: string | undefined;
  assessorIds?: string | undefined;
  providerIds?: string | undefined;
  contractIds?: string | undefined;
}

export const AssessmentsPage = withPermission('canViewAssessments', () => {
  const { currentPage, pageSize, setPage } = usePagination();
  const navigate = useNavigate();
  const { isExpanded, toggleExpandSubSidebar } = useSubSidebar();
  const { assessmentId } = useParams<{ assessmentId: string }>();
  const [searchParams, setSearchParams] = useSearchParams({
    orderBy: 'submitted_at',
    sort: SortOrder.ASC,
    page: '1',
  });
  const [isDrawerExpanded, setIsDrawerExpanded] = useState(false);
  const { filters, updateFilters } = useFiltersContext();

  const querySearch = searchParams.get('search') ?? '';
  const orderBy = searchParams.get('orderBy') ?? 'submitted_at';
  const sort = (searchParams.get('sort') as SortOrder) ?? SortOrder.ASC;
  const courseIds = filters.courses?.length ? filters.courses.join(',') : undefined;
  const assessorIds = filters.assessorIds?.length ? filters.assessorIds.join(',') : undefined;
  const providerIds = filters.providerIds?.length ? filters.providerIds.join(',') : undefined;
  const contractIds = filters.contractIds?.length ? filters.contractIds.join(',') : undefined;
  const outcome = filters.outcome;
  const urlOutcome = searchParams.get('outcome');

  // Sync the URL outcome param (when set outside filter context) via the side panel
  const syncedUrlOutcome = useRef<string | null>(null);
  useEffect(() => {
    if (urlOutcome === syncedUrlOutcome.current) {
      return;
    }
    syncedUrlOutcome.current = urlOutcome;
    updateFilters({ outcome: AssessmentOutcomeSchema.safeParse(urlOutcome).data });
  }, [urlOutcome, updateFilters]);

  const urlParams: SortFilters = useMemo(
    () => ({
      orderBy,
      sort,
      page: searchParams.get('page') ?? undefined,
      search: querySearch !== '' ? querySearch : undefined,
      outcome,
      courseIds: courseIds !== '' ? courseIds : undefined,
      assessorIds: assessorIds !== '' ? assessorIds : undefined,
      providerIds: providerIds !== '' ? providerIds : undefined,
      contractIds: contractIds !== '' ? contractIds : undefined,
    }),
    [
      orderBy,
      sort,
      searchParams,
      querySearch,
      outcome,
      courseIds,
      assessorIds,
      providerIds,
      contractIds,
    ],
  );

  const { data: assessments, isFetching } = trpcAdmin.assessments.all.useQuery(
    {
      search: querySearch.trim(),
      filters: stringifyValues({
        sort,
        orderBy,
        outcome,
        courseIds,
        assessorIds,
        providerIds,
        contractIds,
      }),
      pagination: {
        page: Number(currentPage),
        limit: pageSize,
      },
    },
    {
      initialData: emptyPaginationResponse(),
      placeholderData: (previousData) => previousData,
    },
  );

  const { data: providers } = trpcAdmin.providers.all.useQuery({});
  const { data: users } = trpcAdmin.users.all.useQuery({}, { initialData: [] });

  // Stable reference so it doesn't defeat the memo on each row when filters change.
  const getProviderName = useCallback(
    (providerId: string) => {
      if (providers != null && providers.length > 0 && providerId !== '') {
        const provider = providers.find(({ id }) => id.toString() === providerId);
        if (provider != null) {
          return provider.name;
        }
      }

      return undefined;
    },
    [providers],
  );

  const { isOpen } = useDisclosure({ isOpen: !(assessmentId == null) });

  const handleFilterChange = useCallback(
    (newFilters: Partial<SortFilters>) => {
      const updatedFilters = pickBy(
        { ...Object.fromEntries(searchParams.entries()), ...newFilters, page: undefined },
        (value) => value != null && value != '',
      );

      const newSearchParams = new URLSearchParams(updatedFilters as Record<string, string>);
      setSearchParams(newSearchParams, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const onCloseHandler = useCallback(() => {
    const url = constructURL(
      `/assessments/`,
      Object.fromEntries(searchParams.entries()) as Record<string, string>,
    );
    navigate(url);
    setIsDrawerExpanded(false);
  }, [navigate, searchParams]);

  const drawerActions = (
    <>
      <IconButton
        aria-label={t`toggle fullscreen mode`}
        icon={<Icon as={isDrawerExpanded ? Minimize2 : Maximize2} />}
        onClick={() => setIsDrawerExpanded(!isDrawerExpanded)}
        size="sm"
        variant="ghost"
      />
      <IconButton
        aria-label={t`close drawer`}
        icon={<Icon as={X} />}
        onClick={onCloseHandler}
        size="sm"
        variant="ghost"
      />
    </>
  );

  return (
    <>
      {assessmentId != null ? (
        <Drawer
          isOpen={isOpen}
          onClose={onCloseHandler}
          size={{ base: 'full', lg: isDrawerExpanded ? 'full' : 'xl' }}
        >
          <DrawerOverlay />
          <DrawerContent>
            <Suspense fallback={<CenteredSpinner />}>
              <AssessmentDetails
                assessmentId={assessmentId}
                key={assessmentId}
                drawerActions={drawerActions}
              />
            </Suspense>
          </DrawerContent>
        </Drawer>
      ) : null}

      <TableViewLayout
        pageHeader={[
          !isExpanded && (
            <IconButton
              aria-label={t`expand secondary sidebar`}
              key={'toggle-expand-sub-sidebar'}
              variant="tertiary"
              onClick={toggleExpandSubSidebar}
              size="md"
              icon={<ArrowRightFromLine />}
            />
          ),
          <PageHeading key="page-heading">
            {outcome === 'PENDING' ? (
              <Trans>Pending Assessments</Trans>
            ) : outcome === 'SATISFACTORY' ? (
              <Trans>Satisfactory Assessments</Trans>
            ) : outcome === 'NOT_YET_SATISFACTORY' ? (
              <Trans>Not Yet Satisfactory Assessments</Trans>
            ) : (
              <Trans>All Assessments</Trans>
            )}
          </PageHeading>,
        ]}
        filters={[
          <SearchInput key="search" placeholder={t`Search assessments`} maxW="search-input" />,
          <FilterBarLayout
            key="filter-bar"
            filtersLabel={t`Filters`}
            clearAllLabel={t`Clear All`}
            moreFiltersLabel={t`More filters`}
          >
            <CourseFilter key="course" maxSelected={MAX_FILTER_ENTRIES} />
            <AssessorFilter key="assessor" maxSelected={MAX_FILTER_ENTRIES} />
            <OutcomeFilter key="outcome" />
            <ProvidersFilter key="provider" maxSelected={MAX_FILTER_ENTRIES} />
            <ContractTypeFilter key="contractType" maxSelected={MAX_FILTER_ENTRIES} />
          </FilterBarLayout>,
        ]}
        tableActionsLeft={
          <Text fontSize="md" fontWeight="semibold" color="fg.subtle">
            <Plural
              value={assessments?.pagination.totalCount || 0}
              zero="0 assessments"
              one="1 assessment"
              other="# assessments"
            />
          </Text>
        }
        table={
          <>
            <AssessmentsList
              assessments={assessments.items}
              urlParams={urlParams}
              onFilters={handleFilterChange}
              getProviderName={getProviderName}
              users={users}
            />
            {isFetching ? (
              <Center p={8}>
                <Spinner size="xl" transitionDelay={'2900ms'} />
              </Center>
            ) : assessments.items.length === 0 ? (
              <Center p={8}>
                <Text textAlign="center">
                  <Trans>No assessments found</Trans>
                </Text>
              </Center>
            ) : null}
          </>
        }
        pagination={
          assessments.items.length > 0 ? (
            <Pagination pagination={assessments.pagination} onPageChange={setPage} />
          ) : null
        }
      />
    </>
  );
});
